import React, { useState, useEffect } from 'react';
import axios from 'axios';

const DEFAULT_VAT = 1.19;

const AdminPayments = () => {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [paymentPhoto, setPaymentPhoto] = useState(null);
  const [uploadingPayment, setUploadingPayment] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(0);
  const [editingVat, setEditingVat] = useState(DEFAULT_VAT);
  const [editingAmount, setEditingAmount] = useState(0);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedRoutesForShare, setSelectedRoutesForShare] = useState([]);
  const [shareUserId, setShareUserId] = useState(null);
  const [shareCompanyName, setShareCompanyName] = useState('');
  const [shareAmount, setShareAmount] = useState(0);
  const [shareDiscount, setShareDiscount] = useState(0);
  const [shareVat, setShareVat] = useState(DEFAULT_VAT);
  const [sharing, setSharing] = useState(false);
  const [showFacturaModal, setShowFacturaModal] = useState(false);
  const [selectedFacturaData, setSelectedFacturaData] = useState(null);

  useEffect(() => {
    fetchUnpaidRoutes();
  }, []);

  const fetchUnpaidRoutes = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/admin/payments/unpaid-routes');
      console.log('Fetched unpaid routes:', res.data);
      setRoutes(res.data);
    } catch (error) {
      console.error('Error fetching unpaid routes:', error);
      setRoutes([]);
      if (error.response?.status !== 404) {
        alert('Error al cargar rutas: ' + (error.response?.data?.message || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'completed': { text: 'Completada', class: 'badge-accepted' },
      'rejected': { text: 'Rechazada', class: 'badge-warning' }
    };
    const statusInfo = statusMap[status] || { text: status, class: 'badge-pending' };
    return (
      <span className={`badge ${statusInfo.class}`} style={{ textTransform: 'none' }}>
        {statusInfo.text}
      </span>
    );
  };

  const handleOpenPaymentModal = (route) => {
    // Priority: approved values > route's amount > default price. VAT is fixed.
    if (route.isShared && route.isApproved) {
      setSelectedRoute(route);
      setEditingAmount(route.approvedAmount || route.amount || route.price || 0);
      setEditingDiscount(route.approvedDiscount || 0);
      setEditingVat(DEFAULT_VAT);
    } else {
      setSelectedRoute(route);
      // Use route's amount if set during designation, otherwise use price.
      setEditingAmount(route.amount || route.price || 0);
      setEditingDiscount(0);
      setEditingVat(DEFAULT_VAT);
    }
    setPaymentPhoto(null);
    setShowPaymentModal(true);
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedRoute(null);
    setPaymentPhoto(null);
    setEditingDiscount(0);
    setEditingVat(DEFAULT_VAT);
    setEditingAmount(0);
  };

  const calculateTotal = () => {
    return (editingAmount || 0) - (editingDiscount || 0);
  };

  const calculateToPay = () => {
    const total = calculateTotal();
    return total * DEFAULT_VAT;
  };

  const handlePaymentPhotoChange = (e) => {
    setPaymentPhoto(e.target.files[0]);
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    if (!paymentPhoto) {
      alert('Por favor, selecciona una foto del pago');
      return;
    }

    if (!selectedRoute || !selectedRoute.userId) {
      alert('Error: No se puede identificar el usuario de la ruta');
      return;
    }

    setUploadingPayment(true);
    try {
      const formData = new FormData();
      formData.append('paymentPhoto', paymentPhoto);
      formData.append('selectedRouteIds', selectedRoute._id);
      formData.append('amount', editingAmount);
      formData.append('discount', editingDiscount);
      formData.append('vat', DEFAULT_VAT);

      await axios.post(`/api/admin/payments/${selectedRoute.userId}/pay`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      handleClosePaymentModal();
      fetchUnpaidRoutes();
      alert('Pago registrado exitosamente');
    } catch (error) {
      alert('Error al registrar pago: ' + (error.response?.data?.message || error.message));
    } finally {
      setUploadingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="loading" style={{ textAlign: 'center', padding: '60px' }}>
        Cargando rutas pendientes de pago...
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '30px', 
        flexWrap: 'wrap', 
        gap: '15px',
        paddingBottom: '15px',
        borderBottom: '2px solid #e5e7eb'
      }}>
        <div>
          <h2 style={{ margin: 0, color: '#1e40af', fontSize: '28px', fontWeight: '600' }}>
            💰 A Pagar
          </h2>
          <p style={{ margin: '5px 0 0 0', color: '#6b7280', fontSize: '14px' }}>
            Pagar rutas individuales completadas o rechazadas
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            className="btn btn-info" 
            onClick={() => {
              if (selectedRoutesForShare.length === 0) {
                alert('Por favor, selecciona al menos una ruta para compartir');
                return;
              }
              // Group routes by user
              const routesByUser = {};
              selectedRoutesForShare.forEach(route => {
                if (!routesByUser[route.userId]) {
                  routesByUser[route.userId] = [];
                }
                routesByUser[route.userId].push(route);
              });
              
              // For now, handle first user (can be extended to handle multiple)
              const userIds = Object.keys(routesByUser);
              if (userIds.length > 1) {
                alert('Por favor, selecciona rutas de una sola empresa a la vez');
                return;
              }
              
              const userId = userIds[0];
              const userRoutes = routesByUser[userId];
              const firstRoute = userRoutes[0];
              
              // Calculate initial amount from selected routes - use route's amount if set, otherwise use price
              let calculatedAmount = 0;
              userRoutes.forEach(r => {
                if (r.status === 'completed') {
                  // Use route's amount (set during designation) if available, otherwise use price
                  if (r.amount && r.amount > 0) {
                    calculatedAmount += r.amount;
                  } else {
                    calculatedAmount += r.price || 0;
                  }
                }
              });
              
              setShareUserId(userId);
              setShareCompanyName(firstRoute.companyName);
              setShareAmount(calculatedAmount);
              setShareDiscount(0);
              setShareVat(DEFAULT_VAT);
              setShowShareModal(true);
            }}
            disabled={selectedRoutesForShare.length === 0}
            style={{ 
              minWidth: '140px',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '500',
              borderRadius: '6px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              transition: 'all 0.2s',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              opacity: selectedRoutesForShare.length === 0 ? 0.5 : 1,
              cursor: selectedRoutesForShare.length === 0 ? 'not-allowed' : 'pointer'
            }}
            onMouseEnter={(e) => {
              if (selectedRoutesForShare.length > 0) {
                e.target.style.transform = 'translateY(-1px)';
              }
            }}
            onMouseLeave={(e) => {
              if (selectedRoutesForShare.length > 0) {
                e.target.style.transform = 'translateY(0)';
              }
            }}
          >
            📤 Compartir ({selectedRoutesForShare.length})
          </button>
          <button 
            className="btn btn-secondary" 
            onClick={fetchUnpaidRoutes} 
            style={{ 
              minWidth: '120px',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '500',
              borderRadius: '6px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.transform = 'translateY(-1px)'}
            onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
          >
            🔄 Actualizar
          </button>
        </div>
      </div>

      {routes.length === 0 ? (
        <div className="card" style={{
          textAlign: 'center',
          padding: '60px 20px',
          backgroundColor: '#f9fafb',
          border: '2px dashed #d1d5db',
          borderRadius: '12px'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>💳</div>
          <h3 style={{ color: '#374151', marginBottom: '10px' }}>No hay rutas pendientes de pago</h3>
          <p style={{ color: '#6b7280', margin: 0 }}>
            Las rutas completadas o rechazadas aparecerán aquí cuando estén listas para pagar.
          </p>
        </div>
      ) : (
        <div className="card" style={{
          borderRadius: '12px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          overflow: 'hidden',
          border: '1px solid #e5e7eb'
        }}>
          <div className="table-responsive">
            <table className="data-table" style={{
              width: '100%',
              borderCollapse: 'separate',
              borderSpacing: 0
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f3f4f6' }}>
                  <th style={{ 
                    padding: '16px', 
                    textAlign: 'left', 
                    fontWeight: '600',
                    color: '#374151',
                    borderBottom: '2px solid #e5e7eb',
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Número de Ruta</th>
                  <th style={{ 
                    padding: '16px', 
                    textAlign: 'left', 
                    fontWeight: '600',
                    color: '#374151',
                    borderBottom: '2px solid #e5e7eb',
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Empresa</th>
                  <th style={{ 
                    padding: '16px', 
                    textAlign: 'left', 
                    fontWeight: '600',
                    color: '#374151',
                    borderBottom: '2px solid #e5e7eb',
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Ruta</th>
                  <th style={{ 
                    padding: '16px', 
                    textAlign: 'center', 
                    fontWeight: '600',
                    color: '#374151',
                    borderBottom: '2px solid #e5e7eb',
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Estado</th>
                  <th style={{ 
                    padding: '16px', 
                    textAlign: 'right', 
                    fontWeight: '600',
                    color: '#374151',
                    borderBottom: '2px solid #e5e7eb',
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Precio</th>
                  <th style={{ 
                    padding: '16px', 
                    textAlign: 'center', 
                    fontWeight: '600',
                    color: '#374151',
                    borderBottom: '2px solid #e5e7eb',
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Fecha</th>
                  <th style={{ 
                    padding: '16px', 
                    textAlign: 'center', 
                    fontWeight: '600',
                    color: '#374151',
                    borderBottom: '2px solid #e5e7eb',
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Seleccionar</th>
                  <th style={{ 
                    padding: '16px', 
                    textAlign: 'center', 
                    fontWeight: '600',
                    color: '#374151',
                    borderBottom: '2px solid #e5e7eb',
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {routes.map((route, index) => (
                  <tr 
                    key={route._id}
                    style={{
                      backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9fafb'}
                  >
                    <td style={{ padding: '16px', color: '#1e40af', fontWeight: '600' }}>
                      {route.routeNumber}
                    </td>
                    <td style={{ padding: '16px', color: '#374151' }}>
                      {route.companyName}
                    </td>
                    <td style={{ padding: '16px', color: '#6b7280', fontSize: '14px' }}>
                      {route.startPoint || 'N/A'} → {route.endPoint || 'N/A'}
                    </td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      {getStatusBadge(route.status)}
                    </td>
                    <td style={{ padding: '16px', textAlign: 'right', fontWeight: '600', color: '#059669' }}>
                      {route.isApproved && route.approvedAmount !== null ? (
                        <div>
                          <div style={{ color: '#059669', fontSize: '16px' }}>
                            {formatCurrency(route.approvedAmount)}
                          </div>
                          <div style={{ color: '#6b7280', fontSize: '11px', marginTop: '2px' }}>
                            (Aprobado)
                          </div>
                        </div>
                      ) : (
                        formatCurrency(route.price)
                      )}
                    </td>
                    <td style={{ padding: '16px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>
                      {formatDate(route.createdAt)}
                    </td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectedRoutesForShare.some(r => r._id === route._id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedRoutesForShare([...selectedRoutesForShare, route]);
                          } else {
                            setSelectedRoutesForShare(selectedRoutesForShare.filter(r => r._id !== route._id));
                          }
                        }}
                        style={{
                          width: '18px',
                          height: '18px',
                          cursor: 'pointer'
                        }}
                      />
                    </td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                        {route.isApproved && route.factura && (
                          <button
                            className="btn btn-info"
                            onClick={() => {
                              setSelectedFacturaData({
                                factura: route.factura,
                                approvedAt: route.approvedAt,
                                routeNumber: route.routeNumber,
                                companyName: route.companyName
                              });
                              setShowFacturaModal(true);
                            }}
                            style={{ 
                              padding: '6px 12px', 
                              fontSize: '12px',
                              borderRadius: '6px',
                              fontWeight: '500',
                              transition: 'all 0.2s',
                              border: 'none',
                              backgroundColor: '#3b82f6',
                              color: 'white',
                              cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.transform = 'translateY(-1px)';
                              e.target.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.transform = 'translateY(0)';
                              e.target.style.boxShadow = 'none';
                            }}
                            title="Ver factura y detalles"
                          >
                            📄 Ver Factura
                          </button>
                        )}
                        <button
                          className="btn btn-success"
                          onClick={() => handleOpenPaymentModal(route)}
                          disabled={route.isShared && !route.isApproved}
                          style={{ 
                            padding: '6px 14px', 
                            fontSize: '12px',
                            borderRadius: '6px',
                            fontWeight: '500',
                            transition: 'all 0.2s',
                            border: 'none',
                            cursor: (route.isShared && !route.isApproved) ? 'not-allowed' : 'pointer',
                            opacity: (route.isShared && !route.isApproved) ? 0.5 : 1,
                            backgroundColor: (route.isShared && !route.isApproved) ? '#9ca3af' : '#059669'
                          }}
                          onMouseEnter={(e) => {
                            if (!(route.isShared && !route.isApproved)) {
                              e.target.style.transform = 'translateY(-1px)';
                              e.target.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!(route.isShared && !route.isApproved)) {
                              e.target.style.transform = 'translateY(0)';
                              e.target.style.boxShadow = 'none';
                            }
                          }}
                          title={route.isShared && !route.isApproved ? 'Esperando aprobación del usuario' : ''}
                        >
                          💳 Pagar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedRoute && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '40px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            position: 'relative',
            border: '1px solid #e5e7eb'
          }}>
            <button
              onClick={handleClosePaymentModal}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                cursor: 'pointer',
                fontSize: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#dc2626';
                e.target.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#ef4444';
                e.target.style.transform = 'scale(1)';
              }}
            >
              ×
            </button>
            
            <h3 style={{ 
              marginBottom: '24px', 
              color: '#1e40af',
              fontSize: '24px',
              fontWeight: '600',
              paddingRight: '40px'
            }}>
              💳 Pagar Ruta
            </h3>
            
            <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
              <p style={{ margin: '4px 0', fontSize: '14px', color: '#6b7280' }}>
                <strong>Ruta:</strong> {selectedRoute.routeNumber}
              </p>
              <p style={{ margin: '4px 0', fontSize: '14px', color: '#6b7280' }}>
                <strong>Empresa:</strong> {selectedRoute.companyName}
              </p>
              <p style={{ margin: '4px 0', fontSize: '14px', color: '#6b7280' }}>
                <strong>Estado:</strong> {getStatusBadge(selectedRoute.status)}
              </p>
            </div>

            <form onSubmit={handleSubmitPayment}>
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600', 
                  color: '#374151',
                  fontSize: '14px'
                }}>
                  Monto (CLP)
                </label>
                <input
                  type="number"
                  value={editingAmount}
                  onChange={(e) => setEditingAmount(parseFloat(e.target.value) || 0)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600', 
                  color: '#374151',
                  fontSize: '14px'
                }}>
                  Descuento (CLP)
                </label>
                <input
                  type="number"
                  value={editingDiscount}
                  onChange={(e) => setEditingDiscount(parseFloat(e.target.value) || 0)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600', 
                  color: '#374151',
                  fontSize: '14px'
                }}>
                  IVA (Fijo: 1.19)
                </label>
                <input
                  type="text"
                  value={editingVat}
                  readOnly
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: '#f9fafb',
                    color: '#6b7280',
                    cursor: 'not-allowed'
                  }}
                />
              </div>

              <div style={{ 
                marginBottom: '24px', 
                padding: '16px', 
                backgroundColor: '#eff6ff', 
                borderRadius: '8px',
                border: '1px solid #bfdbfe'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#374151', fontWeight: '500' }}>Total:</span>
                  <span style={{ color: '#1e40af', fontWeight: '600' }}>{formatCurrency(calculateTotal())}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#374151', fontWeight: '500' }}>A Pagar:</span>
                  <span style={{ color: '#059669', fontWeight: '700', fontSize: '18px' }}>
                    {formatCurrency(calculateToPay())}
                  </span>
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '10px', 
                  fontWeight: '600', 
                  color: '#374151',
                  fontSize: '14px'
                }}>
                  Foto del Pago <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <div style={{
                  position: 'relative',
                  border: '2px dashed #d1d5db',
                  borderRadius: '12px',
                  padding: '24px',
                  textAlign: 'center',
                  backgroundColor: '#f9fafb',
                  transition: 'all 0.3s ease',
                  cursor: uploadingPayment ? 'not-allowed' : 'pointer',
                  opacity: uploadingPayment ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!uploadingPayment) {
                    e.currentTarget.style.borderColor = '#3b82f6';
                    e.currentTarget.style.backgroundColor = '#eff6ff';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!uploadingPayment) {
                    e.currentTarget.style.borderColor = '#d1d5db';
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                  }
                }}
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePaymentPhotoChange}
                    disabled={uploadingPayment}
                    style={{
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      top: 0,
                      left: 0,
                      opacity: 0,
                      cursor: uploadingPayment ? 'not-allowed' : 'pointer'
                    }}
                  />
                  {paymentPhoto ? (
                    <div>
                      <div style={{ fontSize: '32px', marginBottom: '8px' }}>📎</div>
                      <p style={{ margin: 0, color: '#059669', fontWeight: '600' }}>
                        {paymentPhoto.name}
                      </p>
                      <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '12px' }}>
                        Click para cambiar
                      </p>
                    </div>
                  ) : (
                    <div>
                      <div style={{ fontSize: '32px', marginBottom: '8px' }}>📤</div>
                      <p style={{ margin: 0, color: '#374151', fontWeight: '600' }}>
                        Arrastra y suelta o click para seleccionar
                      </p>
                      <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '12px' }}>
                        PNG, JPG, PDF hasta 10MB
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={handleClosePaymentModal}
                  disabled={uploadingPayment}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#6b7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: uploadingPayment ? 'not-allowed' : 'pointer',
                    opacity: uploadingPayment ? 0.5 : 1,
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (!uploadingPayment) {
                      e.target.style.backgroundColor = '#4b5563';
                      e.target.style.transform = 'translateY(-1px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!uploadingPayment) {
                      e.target.style.backgroundColor = '#6b7280';
                      e.target.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={uploadingPayment || !paymentPhoto}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: uploadingPayment || !paymentPhoto ? '#9ca3af' : '#059669',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: uploadingPayment || !paymentPhoto ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (!uploadingPayment && paymentPhoto) {
                      e.target.style.backgroundColor = '#047857';
                      e.target.style.transform = 'translateY(-1px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!uploadingPayment && paymentPhoto) {
                      e.target.style.backgroundColor = '#059669';
                      e.target.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {uploadingPayment ? 'Procesando...' : '💳 Pagar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Share Payment Modal */}
      {showShareModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '40px',
            maxWidth: '700px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            position: 'relative',
            border: '1px solid #e5e7eb'
          }}>
            <button
              onClick={() => {
                setShowShareModal(false);
                setSelectedRoutesForShare([]);
                setShareUserId(null);
                setShareCompanyName('');
              }}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                cursor: 'pointer',
                fontSize: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#dc2626';
                e.target.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#ef4444';
                e.target.style.transform = 'scale(1)';
              }}
            >
              ×
            </button>
            
            <h3 style={{ 
              marginBottom: '24px', 
              color: '#1e40af',
              fontSize: '24px',
              fontWeight: '600',
              paddingRight: '40px'
            }}>
              📤 Compartir Pago
            </h3>
            
            <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
              <p style={{ margin: '4px 0', fontSize: '14px', color: '#6b7280' }}>
                <strong>Empresa:</strong> {shareCompanyName}
              </p>
              <p style={{ margin: '4px 0', fontSize: '14px', color: '#6b7280' }}>
                <strong>Rutas seleccionadas:</strong> {selectedRoutesForShare.length}
              </p>
            </div>

            <div style={{ marginBottom: '20px', maxHeight: '200px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px' }}>
              {selectedRoutesForShare.map(route => (
                <div key={route._id} style={{ padding: '8px', borderBottom: '1px solid #e5e7eb', fontSize: '13px' }}>
                  {route.routeNumber} - {route.status === 'completed' ? 'Completada' : 'Rechazada'} - {formatCurrency(route.amount && route.amount > 0 ? route.amount : route.price || 0)}
                </div>
              ))}
            </div>

            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '600', 
                color: '#374151',
                fontSize: '14px'
              }}>
                Monto (CLP)
              </label>
              <input
                type="number"
                value={shareAmount}
                onChange={(e) => setShareAmount(parseFloat(e.target.value) || 0)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '600', 
                color: '#374151',
                fontSize: '14px'
              }}>
                Descuento (CLP)
              </label>
              <input
                type="number"
                value={shareDiscount}
                onChange={(e) => setShareDiscount(parseFloat(e.target.value) || 0)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '600', 
                color: '#374151',
                fontSize: '14px'
              }}>
                IVA (Fijo: 1.19)
              </label>
              <input
                type="text"
                value={shareVat}
                readOnly
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  backgroundColor: '#f9fafb',
                  color: '#6b7280',
                  cursor: 'not-allowed'
                }}
              />
            </div>

            <div style={{ 
              marginBottom: '24px', 
              padding: '16px', 
              backgroundColor: '#eff6ff', 
              borderRadius: '8px',
              border: '1px solid #bfdbfe'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#374151', fontWeight: '500' }}>Total:</span>
                <span style={{ color: '#1e40af', fontWeight: '600' }}>
                  {formatCurrency(shareAmount - shareDiscount)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#374151', fontWeight: '500' }}>A Pagar:</span>
                <span style={{ color: '#059669', fontWeight: '700', fontSize: '18px' }}>
                  {formatCurrency((shareAmount - shareDiscount) * DEFAULT_VAT)}
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => {
                  setShowShareModal(false);
                  setSelectedRoutesForShare([]);
                  setShareUserId(null);
                  setShareCompanyName('');
                }}
                disabled={sharing}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: sharing ? 'not-allowed' : 'pointer',
                  opacity: sharing ? 0.5 : 1,
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!sharing) {
                    e.target.style.backgroundColor = '#4b5563';
                    e.target.style.transform = 'translateY(-1px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!sharing) {
                    e.target.style.backgroundColor = '#6b7280';
                    e.target.style.transform = 'translateY(0)';
                  }
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (shareAmount <= 0) {
                    alert('El monto debe ser mayor a 0');
                    return;
                  }
                  
                  const total = shareAmount - shareDiscount;
                  const toPay = total * DEFAULT_VAT;
                  
                  if (toPay <= 0) {
                    alert('El monto a pagar debe ser mayor a 0');
                    return;
                  }
                  
                  setSharing(true);
                  try {
                    await axios.post(`/api/admin/payments/${shareUserId}/share`, {
                      selectedRouteIds: selectedRoutesForShare.map(r => r._id),
                      amount: shareAmount,
                      discount: shareDiscount,
                      vat: DEFAULT_VAT
                    });
                    
                    alert('Pago compartido exitosamente con el usuario');
                    setShowShareModal(false);
                    setSelectedRoutesForShare([]);
                    setShareUserId(null);
                    setShareCompanyName('');
                    fetchUnpaidRoutes();
                  } catch (error) {
                    alert('Error al compartir pago: ' + (error.response?.data?.message || error.message));
                  } finally {
                    setSharing(false);
                  }
                }}
                disabled={sharing || shareAmount <= 0}
                style={{
                  padding: '12px 24px',
                  backgroundColor: sharing || shareAmount <= 0 ? '#9ca3af' : '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: sharing || shareAmount <= 0 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!sharing && shareAmount > 0) {
                    e.target.style.backgroundColor = '#2563eb';
                    e.target.style.transform = 'translateY(-1px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!sharing && shareAmount > 0) {
                    e.target.style.backgroundColor = '#3b82f6';
                    e.target.style.transform = 'translateY(0)';
                  }
                }}
              >
                {sharing ? 'Compartiendo...' : '📤 Compartir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Factura View Modal */}
      {showFacturaModal && selectedFacturaData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '40px',
            maxWidth: '900px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            position: 'relative',
            border: '1px solid #e5e7eb'
          }}>
            <button
              onClick={() => {
                setShowFacturaModal(false);
                setSelectedFacturaData(null);
              }}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                cursor: 'pointer',
                fontSize: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#dc2626';
                e.target.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#ef4444';
                e.target.style.transform = 'scale(1)';
              }}
            >
              ×
            </button>
            
            <h3 style={{ 
              marginBottom: '24px', 
              color: '#1e40af',
              fontSize: '24px',
              fontWeight: '600',
              paddingRight: '40px'
            }}>
              📄 Factura del Usuario
            </h3>
            
            <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
              <p style={{ margin: '4px 0', fontSize: '14px', color: '#6b7280' }}>
                <strong>Ruta:</strong> {selectedFacturaData.routeNumber}
              </p>
              <p style={{ margin: '4px 0', fontSize: '14px', color: '#6b7280' }}>
                <strong>Empresa:</strong> {selectedFacturaData.companyName}
              </p>
              {selectedFacturaData.approvedAt && (
                <p style={{ margin: '4px 0', fontSize: '14px', color: '#6b7280' }}>
                  <strong>Aprobado el:</strong> {formatDate(selectedFacturaData.approvedAt)} {new Date(selectedFacturaData.approvedAt).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
            </div>
            
            {selectedFacturaData.factura && selectedFacturaData.factura.path && (
              <div style={{
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                padding: '20px',
                backgroundColor: '#f9fafb',
                textAlign: 'center',
                marginBottom: '20px'
              }}>
                {selectedFacturaData.factura.filename && selectedFacturaData.factura.filename.toLowerCase().endsWith('.pdf') ? (
                  <iframe
                    src={selectedFacturaData.factura.path}
                    style={{
                      width: '100%',
                      height: '600px',
                      border: 'none',
                      borderRadius: '8px'
                    }}
                    title="Factura PDF"
                  />
                ) : (
                  <img
                    src={selectedFacturaData.factura.path}
                    alt="Factura"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '600px',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                )}
                <div style={{ 
                  display: 'none',
                  padding: '20px',
                  color: '#ef4444'
                }}>
                  Error al cargar la factura. Por favor, intenta descargarla directamente.
                </div>
                
                <div style={{ marginTop: '16px' }}>
                  <a
                    href={selectedFacturaData.factura.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-block',
                      padding: '10px 20px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      textDecoration: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#2563eb';
                      e.target.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#3b82f6';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    📥 Descargar Factura
                  </a>
                </div>
                
                {selectedFacturaData.factura.uploadedAt && (
                  <p style={{ 
                    marginTop: '12px',
                    fontSize: '12px',
                    color: '#6b7280'
                  }}>
                    Subida el: {new Date(selectedFacturaData.factura.uploadedAt).toLocaleString('es-CL')}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPayments;
